import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import {
  FolderIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  UserIcon,
  BriefcaseIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore, triggerRefresh } from '../../stores/fileStore';
import {
  foldersApi,
  type MoveDestinationFolder,
  type MoveDestinationClient,
  type MoveDestinationProject,
} from '../../api/folders';
import { filesApi } from '../../api/files';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Spinner } from '../ui/Spinner';
import { useToast } from '../ui/Toaster';
import clsx from 'clsx';

interface FolderTreeItem extends MoveDestinationFolder {
  children?: FolderTreeItem[];
}

// Build folder tree from flat list
function buildFolderTree(folders: MoveDestinationFolder[]): FolderTreeItem[] {
  const folderMap = new Map<string, FolderTreeItem>();
  const rootFolders: FolderTreeItem[] = [];

  // First pass: create all folder items
  folders.forEach((folder) => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // Second pass: build tree structure
  folders.forEach((folder) => {
    const item = folderMap.get(folder.id)!;
    if (folder.parentId && folderMap.has(folder.parentId)) {
      folderMap.get(folder.parentId)!.children!.push(item);
    } else {
      rootFolders.push(item);
    }
  });

  return rootFolders;
}

export function MoveToFolderModal() {
  const { t } = useTranslation('files');
  const [personalFolders, setPersonalFolders] = useState<FolderTreeItem[]>([]);
  const [clients, setClients] = useState<MoveDestinationClient[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  // Expansion state for sections and items
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['personal'])
  );
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const isOpen = useUIStore((s) => s.isMoveModalOpen);
  const closeModal = useUIStore((s) => s.closeMoveModal);
  const moveFileIds = useUIStore((s) => s.moveModalFileIds);

  const currentFolderId = useFileStore((s) => s.currentFolderId);
  const files = useFileStore((s) => s.files);
  const clearSelection = useFileStore((s) => s.clearSelection);
  const toast = useToast();

  const filesToMove = files.filter((f) => moveFileIds.includes(f.id));

  // Track if we've already loaded for this modal opening
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load all move destinations
  useEffect(() => {
    if (isOpen && !hasLoaded) {
      setHasLoaded(true);
      setIsLoading(true);
      setSelectedFolderId(null);
      setExpandedSections(new Set(['personal']));
      setExpandedClients(new Set());
      setExpandedProjects(new Set());
      setExpandedFolders(new Set());

      foldersApi.getMoveDestinations()
        .then((data) => {
          setPersonalFolders(buildFolderTree(data.personalFolders));
          setClients(data.clients);
        })
        .catch(() => {
          toast.error(t('toast.loadFoldersFailed'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    // Reset hasLoaded when modal closes
    if (!isOpen && hasLoaded) {
      setHasLoaded(false);
    }
  }, [isOpen, hasLoaded, toast, t]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const toggleClient = (clientId: string) => {
    setExpandedClients((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const toggleProject = (projectId: string) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleMove = async () => {
    if (moveFileIds.length === 0) return;

    setIsMoving(true);
    try {
      await filesApi.bulkAction({
        fileIds: moveFileIds,
        action: 'move',
        folderId: selectedFolderId || undefined,
      });

      toast.success(
        t('toast.filesMoved'),
        t('toast.filesMovedDesc', { count: moveFileIds.length })
      );
      clearSelection();
      triggerRefresh();
      handleClose();
    } catch {
      toast.error(t('toast.moveFailed'));
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setPersonalFolders([]);
    setClients([]);
    setSelectedFolderId(null);
    closeModal();
  };

  const renderFolder = (folder: FolderTreeItem, depth: number = 0) => {
    const isSelected = selectedFolderId === folder.id;
    const isCurrentFolder = currentFolderId === folder.id;
    const hasChildren = folder.children && folder.children.length > 0;
    const isExpanded = expandedFolders.has(folder.id);

    return (
      <div key={folder.id}>
        <div
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors',
            isSelected
              ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
              : 'hover:bg-gray-100 dark:hover:bg-slate-700',
            isCurrentFolder && 'opacity-50 cursor-not-allowed'
          )}
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
          onClick={() => !isCurrentFolder && setSelectedFolderId(folder.id)}
        >
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleFolder(folder.id);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <FolderIcon
            className="w-5 h-5 flex-shrink-0"
            style={{ color: folder.color || '#6b7280' }}
          />

          <span className="flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
            {folder.name}
          </span>

          {isCurrentFolder && (
            <span className="text-xs text-gray-400 dark:text-slate-500">
              ({t('move.current')})
            </span>
          )}
        </div>

        <AnimatePresence>
          {isExpanded && hasChildren && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {folder.children!.map((child) => renderFolder(child, depth + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderProject = (project: MoveDestinationProject, depth: number = 0) => {
    const isExpanded = expandedProjects.has(project.id);
    const hasContent = project.folders.length > 0;
    const projectFolders = buildFolderTree(project.folders);

    return (
      <div key={project.id}>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
          style={{ paddingLeft: `${depth * 16 + 12}px` }}
          onClick={() => hasContent && toggleProject(project.id)}
        >
          {hasContent ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleProject(project.id);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <BriefcaseIcon className="w-5 h-5 text-blue-500 flex-shrink-0" />

          <span className="flex-1 truncate text-sm font-medium text-gray-900 dark:text-white">
            {project.name}
          </span>

          {!hasContent && (
            <span className="text-xs text-gray-400 dark:text-slate-500">
              ({t('move.noFolders')})
            </span>
          )}
        </div>

        <AnimatePresence>
          {isExpanded && hasContent && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {projectFolders.map((folder) => renderFolder(folder, depth + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const renderClient = (client: MoveDestinationClient) => {
    const isExpanded = expandedClients.has(client.id);
    const hasProjects = client.projects.length > 0;

    return (
      <div key={client.id}>
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
          onClick={() => hasProjects && toggleClient(client.id)}
        >
          {hasProjects ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleClient(client.id);
              }}
              className="p-0.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
              )}
            </button>
          ) : (
            <div className="w-5" />
          )}

          <UserIcon className="w-5 h-5 text-purple-500 flex-shrink-0" />

          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate block">
              {client.name}
            </span>
            {client.company && (
              <span className="text-xs text-gray-500 dark:text-slate-400 truncate block">
                {client.company}
              </span>
            )}
          </div>

          {!hasProjects && (
            <span className="text-xs text-gray-400 dark:text-slate-500">
              ({t('move.noProjects')})
            </span>
          )}
        </div>

        <AnimatePresence>
          {isExpanded && hasProjects && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              {client.projects.map((project) => renderProject(project, 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('move.title')}
      size="md"
    >
      <div className="space-y-4">
        {/* Info */}
        <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
          <p className="text-sm text-gray-600 dark:text-slate-400">
            {t('move.movingFiles', { count: filesToMove.length })}
          </p>
          {filesToMove.length <= 3 && (
            <div className="mt-2 space-y-1">
              {filesToMove.map((f) => (
                <p
                  key={f.id}
                  className="text-sm font-medium text-gray-900 dark:text-white truncate"
                >
                  {f.name}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Destinations */}
        <div className="max-h-80 overflow-y-auto border border-gray-200 dark:border-slate-700 rounded-lg">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : (
            <div className="p-2 space-y-2">
              {/* Personal Files Section */}
              <div>
                <div
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                  onClick={() => toggleSection('personal')}
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection('personal');
                    }}
                    className="p-0.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                  >
                    {expandedSections.has('personal') ? (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    )}
                  </button>

                  <HomeIcon className="w-5 h-5 text-gray-500 dark:text-slate-400 flex-shrink-0" />

                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {t('move.myFiles')}
                  </span>
                </div>

                <AnimatePresence>
                  {expandedSections.has('personal') && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4"
                    >
                      {/* Root option */}
                      <div
                        className={clsx(
                          'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ml-5',
                          selectedFolderId === null
                            ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300'
                            : 'hover:bg-gray-100 dark:hover:bg-slate-700',
                          currentFolderId === null && 'opacity-50 cursor-not-allowed'
                        )}
                        onClick={() =>
                          currentFolderId !== null && setSelectedFolderId(null)
                        }
                      >
                        <div className="w-5" />
                        <FolderIcon className="w-5 h-5 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {t('move.myFilesRoot')}
                        </span>
                        {currentFolderId === null && (
                          <span className="text-xs text-gray-400 dark:text-slate-500">
                            ({t('move.current')})
                          </span>
                        )}
                      </div>

                      {/* Personal folders */}
                      {personalFolders.map((folder) => renderFolder(folder, 1))}

                      {personalFolders.length === 0 && (
                        <p className="text-sm text-gray-500 dark:text-slate-400 py-2 pl-10">
                          {t('move.noFolders')}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Clients Section */}
              {clients.length > 0 && (
                <div>
                  <div
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 cursor-pointer transition-colors"
                    onClick={() => toggleSection('clients')}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection('clients');
                      }}
                      className="p-0.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                    >
                      {expandedSections.has('clients') ? (
                        <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                      )}
                    </button>

                    <UserIcon className="w-5 h-5 text-purple-500 flex-shrink-0" />

                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {t('move.clients')}
                    </span>
                  </div>

                  <AnimatePresence>
                    {expandedSections.has('clients') && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="ml-4"
                      >
                        {clients.map((client) => renderClient(client))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            className="flex-1"
          >
            {t('common:buttons.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleMove}
            isLoading={isMoving}
            disabled={
              (selectedFolderId === null && currentFolderId === null) ||
              selectedFolderId === currentFolderId
            }
            className="flex-1"
          >
            {t('move.moveHere')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
